import { fetchBot } from '@/services/services';
import { FindAvailabilityRequest, FindAvailabilityWindow } from '@bariendo/lib/scheduling';
import { getOperationOutcomeMessage } from '@bariendo/lib/error';
import { MedplumClient } from '@medplum/core';
import { Practitioner } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { ProcedureDateOption, toProcedureDateOptions } from './procedureDateOptions';

/**
 * The procedure types a patient can be offered a date for. Removals are excluded:
 * an IGB removal is scheduled off an existing balloon, not sold on a hand-off call.
 */
export const PROCEDURE_APPOINTMENT_TYPES = [
  'esg-procedure',
  'esg-max-procedure',
  'sis-procedure',
  'tore-regular-procedure',
  'tore-plus-procedure',
  'igb-placement-procedure',
];

/**
 * How far ahead to look for procedure blocks.
 *
 * Ninety days rather than thirty: blocks are laid down in monthly batches, so a
 * short window shows an empty grid in the gap between batches — which a rep reads
 * as "no availability" rather than "look further out".
 */
export const PROCEDURE_LOOKAHEAD_DAYS = 90;

export interface ProcedureBlockDates {
  options: ProcedureDateOption[];
  loading: boolean;
  error: string | undefined;
}

async function resolvePhysicianNames(
  medplum: MedplumClient,
  options: ProcedureDateOption[]
): Promise<ProcedureDateOption[]> {
  const references = [...new Set(options.map((option) => option.physicianReference).filter(Boolean))] as string[];
  if (references.length === 0) return options;

  const names = new Map<string, string>();
  await Promise.all(
    references.map(async (reference) => {
      try {
        const practitioner = await medplum.readReference<Practitioner>({ reference });
        const name = practitioner.name?.[0];
        const display = [name?.given?.join(' '), name?.family].filter(Boolean).join(' ');
        if (display) names.set(reference, display);
      } catch {
        // A missing name degrades the chip label; it must not drop the date.
      }
    })
  );

  return options.map((option) => ({
    ...option,
    ...(option.physicianReference && names.has(option.physicianReference)
      ? { physicianName: names.get(option.physicianReference) }
      : {}),
  }));
}

/**
 * The procedure days on offer for the "Discuss timing" beat, read from the
 * physicians' block schedules.
 *
 * Reuses `find-availability` — the same bot the intro call uses for consultation
 * slots — because procedure "time blocks" are the same one-time availability
 * `Slot`s it already reads. Nothing here books: the chosen day is recorded on the
 * note and the CC team schedules once payment completes.
 *
 * SCOPE CAVEAT: `locationReferences` is optional and currently unset by the card.
 * There is no patient-to-site helper in this repo, and `FindAvailabilityWindow`
 * carries a practitioner but no location, so a site cannot be labelled from the
 * response either. Unscoped, this returns the next blocks across *every* procedure
 * site — wrong for a patient who can only travel to one of them. Passing the
 * patient's site through is the outstanding input on BAR-2405; the parameter
 * exists so that lands as a one-line change rather than a rewrite.
 */
export interface ProcedureBlockDatesOptions {
  /**
   * Days from today before the first date offered. The PN consultation uses 14:
   * a payment, a blood panel and a provider visit sit between the call and the
   * procedure, so a nearer date cannot be kept.
   */
  minLeadDays?: number;
}

export function useProcedureBlockDates(
  enabled: boolean,
  locationReferences?: string[],
  { minLeadDays = 0 }: ProcedureBlockDatesOptions = {}
): ProcedureBlockDates {
  const medplum = useMedplum();
  const [options, setOptions] = useState<ProcedureDateOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const locationKey = locationReferences?.join(',') ?? '';

  useEffect(() => {
    // Only once the rep opens the timing beat. Most of the call happens elsewhere
    // and a 90-day availability read is not free.
    if (!enabled) return;

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(undefined);

      try {
        const now = dayjs();
        const request: FindAvailabilityRequest = {
          serviceTypes: PROCEDURE_APPOINTMENT_TYPES,
          startTime: now.add(minLeadDays, 'day').startOf('day').toISOString(),
          endTime: now.add(PROCEDURE_LOOKAHEAD_DAYS, 'day').endOf('day').toISOString(),
          ...(locationKey ? { locations: locationKey.split(',') } : {}),
        };

        const windows = (await fetchBot(medplum, 'find-availability', request)) as FindAvailabilityWindow[];
        const withNames = await resolvePhysicianNames(medplum, toProcedureDateOptions(windows ?? []));
        if (!cancelled) setOptions(withNames);
      } catch (caught) {
        // Surfaced in the beat rather than as a toast: the rep is mid-sentence, and
        // "couldn't load dates" is information they need in front of them.
        if (!cancelled) {
          setOptions([]);
          setError(getOperationOutcomeMessage(caught));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [medplum, enabled, locationKey, minLeadDays]);

  return { options, loading, error };
}
